import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { keyBy } from "lodash";
import { Loader2 } from "lucide-react";
import { useQuery } from "urql";
import { useLocation, useSearchParams } from "wouter";
import { OpenTimeSeries } from "@captor/openseries-ts";

import { DateHelper, emptyObjectId } from "../../../../common/src";
import { ClientContext, ClientSelectorPlaceHolder } from "../../../../components/src/contexts/ClientContext";
import { Label } from "../../../../components/src/form";
import { useQueryStateLazy } from "../../../../components/src/use-query-state";
import { getClients, getInstrumentValuations, getPerformanceData } from "../../../../graphql/src/performance/queries";
import { ClientContextSelector } from "../../contexts/ClientContextSelector";
import { CurrencyEnum, Party, PartyAccount, PartyAccountType, PartyStatusEnum, PartyType } from "../../types.generated";
import { CompareAnnualBarChart } from "../CompareAnnualBarChart";
import { CompareMetricsTable } from "../CompareMetricsTable";
import { PerformanceKpiStrip } from "../performance/PerformanceKpiStrip";
import { PerformancePeriodSelector } from "../performance/PerformancePeriodSelector";
import { isValidRow } from "../performance/functions";
import { useProcessedData } from "../performance/hooks";
import { ProcessedData, SelectedRowType } from "../performance/interfaces";
import { TimeSeriePlot } from "../timeseries/TimeSeriePlot";
import {
    accountIdsEqual,
    accountOptionLabel,
    buildConstituentSeries,
    buildVsIndicesSeries,
    defaultOneYearStartDate,
    extraHeldSeriesFromValuations,
    getAccountSelectedRow,
    getInstrumentIdsForValuations,
    getMissingHeldInstrumentIds,
    getSelectableAccounts,
    groupValuationPrices,
    shouldGroupInstrumentPerformance,
    contributionWaterfallItems
} from "./accountPerformanceUtils";
import { ContributionWaterfall } from "./ContributionWaterfall";

/** Muted qualitative hues for overlapping series. Slate is reserved for the reference column. */
const SERIES_COLORWAY = ["#3B6B8C", "#C47B4A", "#3F7A62", "#8B5E83", "#B0892E", "#9B4D5A", "#4F6F4A"];
const REFERENCE_COLOR = "#6B7280";

function colorsForSeries(count: number, referenceColumn?: number): string[] {
    const colors: string[] = [];
    let hueIndex = 0;
    for (let i = 0; i < count; i++) {
        if (count > 1 && typeof referenceColumn === "number" && i === referenceColumn) {
            colors.push(REFERENCE_COLOR);
        } else {
            colors.push(SERIES_COLORWAY[hueIndex % SERIES_COLORWAY.length]);
            hueIndex += 1;
        }
    }
    return colors;
}

const PLOT_MARGIN = { t: 0, b: 0, l: 8, r: 8, pad: 0 };

/** Idle time after the last grouping/contribution tick before the report remounts. */
export const DISPLAY_OPTION_DEBOUNCE_MS = 450;

function LoadingSpinner({ label = "Loading" }: { label?: string }): React.ReactElement {
    return (
        <div className="flex items-center gap-2 text-slate-600" role="status" aria-live="polite">
            <Loader2 className="h-5 w-5 animate-spin shrink-0" aria-hidden="true" />
            <span className="text-sm">{label}</span>
        </div>
    );
}

const CompareReportBlock = React.memo(function CompareReportBlock({
    title,
    timeseries,
    referenceColumn,
    startDate,
    endDate,
    waterfallItems
}: {
    title: string;
    timeseries: OpenTimeSeries[];
    referenceColumn?: number;
    startDate: string;
    endDate: string;
    waterfallItems?: { name: string; value: number }[];
}): React.ReactElement {
    const colors = useMemo(() => colorsForSeries(timeseries.length, referenceColumn), [timeseries.length, referenceColumn]);
    const instrumentIds = useMemo(() => timeseries.map(() => ""), [timeseries]);
    const showWaterfall = Array.isArray(waterfallItems) && waterfallItems.length > 0;
    const showCharts = timeseries.length > 0 || showWaterfall;
    return (
        <div className="mt-6">
            <h4 className="text-slate-900 font-semibold mb-3 normal-case">{title}</h4>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                <div className="lg:col-span-2 flex flex-col gap-4 min-h-[560px]">
                    {showCharts ? (
                        <Fragment>
                            <div className="bg-white rounded border border-gray-200 overflow-hidden flex-[3] min-h-[280px] relative">
                                {showWaterfall ? (
                                    <ContributionWaterfall
                                        items={waterfallItems}
                                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                                    />
                                ) : (
                                    <TimeSeriePlot
                                        tss={timeseries}
                                        instrumentIds={instrumentIds}
                                        useRangeSelector={false}
                                        customMargin={PLOT_MARGIN}
                                        colors={colors}
                                        asPercentFromOne
                                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                                    />
                                )}
                            </div>
                            <div className="bg-white rounded border border-gray-200 overflow-hidden flex-[2] min-h-[260px] relative">
                                <CompareAnnualBarChart
                                    timeseries={timeseries}
                                    colors={colors}
                                    startDate={startDate}
                                    endDate={endDate}
                                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                                />
                            </div>
                        </Fragment>
                    ) : (
                        <p className="text-gray-500">No series to display</p>
                    )}
                </div>
                <div className="lg:col-span-1 min-h-[560px] flex flex-col">
                    {timeseries.length > 0 ? (
                        <div className="bg-white rounded border border-gray-200 p-4 flex flex-col flex-1 overflow-auto">
                            <div className="flex-1 min-h-0 overflow-auto">
                                <CompareMetricsTable timeseries={timeseries} referenceColumn={referenceColumn} />
                            </div>
                        </div>
                    ) : (
                        <div className="text-gray-500 text-sm">Metrics appear when an account is selected</div>
                    )}
                </div>
            </div>
        </div>
    );
});

function AccountPerformanceReport({
    client,
    processedData,
    account,
    startDate,
    endDate,
    groupInstruments,
    accountContribution
}: {
    client: Partial<Party>;
    processedData: ProcessedData;
    account: Partial<PartyAccount>;
    startDate: string;
    endDate: string;
    groupInstruments: boolean;
    accountContribution: boolean;
}): React.ReactElement {
    const accountName = account.description || account.name || account._id;
    const includeModelIndex = account.type !== PartyAccountType.Physical;

    const selectedRow = useMemo(() => {
        if (!processedData) return null;
        return getAccountSelectedRow(account._id, accountName, processedData, startDate, endDate, includeModelIndex);
    }, [processedData, account._id, accountName, startDate, endDate, includeModelIndex]);

    const vsIndices = useMemo(() => {
        if (!selectedRow || !isValidRow(selectedRow)) return [];
        return buildVsIndicesSeries(selectedRow, startDate, endDate);
    }, [selectedRow, startDate, endDate]);

    const applyInstrumentGrouping = shouldGroupInstrumentPerformance(account, processedData, endDate, groupInstruments, {
        includeHistorical: accountContribution
    });
    const skipHoldingFallbacks = applyInstrumentGrouping || accountContribution;

    const missingHeldIds = useMemo(() => {
        if (skipHoldingFallbacks) return [];
        if (!processedData || account.type !== PartyAccountType.Physical) return [];
        return getMissingHeldInstrumentIds(account._id, processedData);
    }, [processedData, account._id, account.type, skipHoldingFallbacks]);

    const valuationInstrumentIds = useMemo(() => {
        if (skipHoldingFallbacks) return [];
        if (!processedData || account.type !== PartyAccountType.Physical) return [];
        return getInstrumentIdsForValuations(account._id, processedData, endDate);
    }, [processedData, account._id, account.type, endDate, skipHoldingFallbacks]);

    const [{ data: valuationData }] = useQuery({
        query: getInstrumentValuations,
        variables: { instrumentIds: valuationInstrumentIds, startDate, endDate },
        pause: valuationInstrumentIds.length === 0
    });

    const valuationPricesById = useMemo(() => groupValuationPrices(valuationData?.valuations || []), [valuationData]);

    const extraHeldSeries = useMemo(() => {
        if (!processedData || account.type !== PartyAccountType.Physical || missingHeldIds.length === 0) return [];
        return extraHeldSeriesFromValuations(account._id, processedData, valuationPricesById, startDate, endDate);
    }, [processedData, account._id, account.type, missingHeldIds.length, valuationPricesById, startDate, endDate]);

    const constituents = useMemo(() => {
        if (!processedData) return [];
        return buildConstituentSeries(
            account,
            accountName,
            processedData,
            startDate,
            endDate,
            extraHeldSeries,
            valuationPricesById,
            groupInstruments,
            accountContribution
        );
    }, [processedData, account, accountName, startDate, endDate, extraHeldSeries, valuationPricesById, groupInstruments, accountContribution]);

    const showContributionWaterfall = accountContribution && account.type === PartyAccountType.Physical;
    let constituentTitle = "Child Portfolio Performance";
    if (showContributionWaterfall) {
        constituentTitle = "Portfolio Holdings Contribution";
    } else if (account.type === PartyAccountType.Physical) {
        constituentTitle = "Portfolio Holdings Performance";
    }

    if (!client.firstTradeDate) return <p>Missing firstTradeDate</p>;
    if (!processedData || !processedData.dateSeries || processedData.dateSeries.length === 0) {
        return <p className="text-gray-500 mb-0">No performance data for this client.</p>;
    }

    const selectedRowWithDates: SelectedRowType | null = selectedRow ? { ...selectedRow, startDate, endDate } : null;
    const constituentReference = constituents.length > 0 ? constituents.length - 1 : 0;
    const vsIndicesReference = vsIndices.length > 1 ? 1 : 0;
    const holdingsWaterfallItems = showContributionWaterfall ? contributionWaterfallItems(constituents, accountName) : undefined;

    return (
        <div>
            {selectedRowWithDates && isValidRow(selectedRowWithDates) ? <PerformanceKpiStrip selectedRow={selectedRowWithDates} /> : null}

            <CompareReportBlock
                title="Portfolio Performance"
                timeseries={vsIndices}
                referenceColumn={vsIndicesReference}
                startDate={startDate}
                endDate={endDate}
            />
            <CompareReportBlock
                title={constituentTitle}
                timeseries={constituents}
                referenceColumn={constituentReference}
                startDate={startDate}
                endDate={endDate}
                waterfallItems={holdingsWaterfallItems}
            />
        </div>
    );
}

export function AccountPerformancePage(): React.ReactElement {
    const { client: clientContext, setClient } = useContext(ClientContext);
    const [, navigate] = useLocation();
    const yesterday: string = DateHelper.dateToString(DateHelper.previousWeekday());
    const [endDate] = useQueryStateLazy("endDate", yesterday);
    const [queryArgs, setQueryArgs] = useSearchParams();
    const [accountId, setAccountId] = useState(() => queryArgs.get("account") || "");

    const applyAccountId = useCallback(
        (nextId: string) => {
            setAccountId(nextId);
            setQueryArgs((prev) => {
                const next = new URLSearchParams(prev);
                if (nextId) next.set("account", nextId);
                else next.delete("account");
                return next;
            });
        },
        [setQueryArgs]
    );

    const [{ data, fetching: loading, error }] = useQuery<{ clients?: Partial<Party>[] }>({
        query: getClients,
        requestPolicy: "cache-first"
    });

    const { client }: { client: Partial<Party> } = useMemo(() => {
        let client = null;
        if (data) {
            const clientsById = keyBy(data.clients, "_id");
            client = clientsById[clientContext._id] || null;
        }
        return { client };
    }, [data, clientContext]);

    const defaultStartDate = defaultOneYearStartDate(endDate, client?.firstTradeDate);
    const [startDate] = useQueryStateLazy("startDate", defaultStartDate);
    const [instrumentGrouping, setInstrumentGrouping] = useQueryStateLazy("instrumentGrouping", "true");
    const urlGroupInstruments = instrumentGrouping !== "false";
    const [accountContributionParam, setAccountContributionParam] = useQueryStateLazy("accountContribution", "true");
    const urlAccountContribution = accountContributionParam !== "false";
    const [draftGroupInstruments, setDraftGroupInstruments] = useState(urlGroupInstruments);
    const [draftAccountContribution, setDraftAccountContribution] = useState(urlAccountContribution);
    const [appliedGroupInstruments, setAppliedGroupInstruments] = useState(urlGroupInstruments);
    const [appliedAccountContribution, setAppliedAccountContribution] = useState(urlAccountContribution);
    const skipGroupingUrlSyncRef = useRef(false);
    const skipContributionUrlSyncRef = useRef(false);

    useEffect(() => {
        if (skipGroupingUrlSyncRef.current) {
            skipGroupingUrlSyncRef.current = false;
            return;
        }
        setDraftGroupInstruments(urlGroupInstruments);
        setAppliedGroupInstruments(urlGroupInstruments);
    }, [urlGroupInstruments]);

    useEffect(() => {
        if (skipContributionUrlSyncRef.current) {
            skipContributionUrlSyncRef.current = false;
            return;
        }
        setDraftAccountContribution(urlAccountContribution);
        setAppliedAccountContribution(urlAccountContribution);
    }, [urlAccountContribution]);

    useEffect(() => {
        if (draftGroupInstruments === appliedGroupInstruments && draftAccountContribution === appliedAccountContribution) {
            return;
        }
        const timeoutId = window.setTimeout(() => {
            const groupingValue = draftGroupInstruments ? "true" : "false";
            const contributionValue = draftAccountContribution ? "true" : "false";
            skipGroupingUrlSyncRef.current = groupingValue !== instrumentGrouping;
            skipContributionUrlSyncRef.current = contributionValue !== accountContributionParam;
            setAppliedGroupInstruments(draftGroupInstruments);
            setAppliedAccountContribution(draftAccountContribution);
            if (skipGroupingUrlSyncRef.current) setInstrumentGrouping(groupingValue);
            if (skipContributionUrlSyncRef.current) setAccountContributionParam(contributionValue);
        }, DISPLAY_OPTION_DEBOUNCE_MS);
        return () => window.clearTimeout(timeoutId);
    }, [
        draftGroupInstruments,
        draftAccountContribution,
        appliedGroupInstruments,
        appliedAccountContribution,
        instrumentGrouping,
        accountContributionParam,
        setInstrumentGrouping,
        setAccountContributionParam
    ]);

    // Same payload as /performance: one client-wide fetch. cache-first so changing account or startDate
    // does not refetch. A new endDate or client id is a new cache key and loads once.
    const [{ data: dataPerformance, error: errorPerformance, fetching: fetchingPerformance }] = useQuery({
        query: getPerformanceData,
        variables: {
            clientId: client?._id,
            endDate: endDate,
            endDateString: endDate,
            accountingCurrency: client?.accountingCurrency ? (client.accountingCurrency as CurrencyEnum) : CurrencyEnum.SEK
        },
        requestPolicy: "cache-first",
        pause: !client?._id
    });

    const lastPerformanceRef = useRef<typeof dataPerformance>(undefined);
    const lastClientIdRef = useRef<string | null>(null);
    if (!client?._id) {
        lastClientIdRef.current = null;
        lastPerformanceRef.current = undefined;
    } else if (client._id !== lastClientIdRef.current) {
        lastClientIdRef.current = client._id;
        lastPerformanceRef.current = undefined;
    }
    const performanceMatchesClient = Boolean(dataPerformance && client?._id && dataPerformance.client?._id === client._id);
    if (performanceMatchesClient) {
        lastPerformanceRef.current = dataPerformance;
    }
    let effectivePerformanceData = lastPerformanceRef.current;
    if (!client?._id) {
        effectivePerformanceData = undefined;
    } else if (performanceMatchesClient) {
        effectivePerformanceData = dataPerformance;
    }

    // Build the full client tree as soon as the payload arrives — before an account is chosen —
    // so later account and period changes only slice in-memory series.
    const processedData: ProcessedData = useProcessedData({
        data: effectivePerformanceData,
        endDate,
        startDate: client?.firstTradeDate || endDate
    });

    const selectableAccounts = useMemo(() => getSelectableAccounts(client?.accounts), [client]);

    const selectedAccount: Partial<PartyAccount> | null = useMemo(() => {
        if (!accountId || !selectableAccounts.length) return null;
        return selectableAccounts.find((account) => accountIdsEqual(account._id, accountId)) || null;
    }, [accountId, selectableAccounts]);

    useEffect(() => {
        if (loading || !client || !accountId) return;
        if (!Array.isArray(client.accounts)) return;
        if (!selectableAccounts.some((account) => accountIdsEqual(account._id, accountId))) {
            applyAccountId("");
        }
    }, [loading, client, accountId, selectableAccounts, applyAccountId]);

    const clientSelected = Boolean(clientContext.dashName && clientContext._id !== emptyObjectId);
    const isLoadingPerformance = Boolean(clientSelected && fetchingPerformance);
    const reportWorkKey = selectedAccount
        ? `${String(selectedAccount._id)}|${startDate}|${endDate}|${appliedGroupInstruments}|${appliedAccountContribution}`
        : "";

    const handleClear = useCallback(() => {
        lastPerformanceRef.current = undefined;
        lastClientIdRef.current = null;
        setAccountId("");
        setDraftGroupInstruments(true);
        setDraftAccountContribution(true);
        setAppliedGroupInstruments(true);
        setAppliedAccountContribution(true);
        setClient(ClientSelectorPlaceHolder);
        setQueryArgs(new URLSearchParams());
        navigate("/accountperformance");
    }, [navigate, setClient, setQueryArgs]);

    if (loading && !data) {
        return (
            <div className="p-4">
                <LoadingSpinner label="Loading" />
            </div>
        );
    }

    if (error)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    return (
        <div id="accountperformancepage">
            <div className="flex flex-wrap -mx-2 print-none">
                <div className="w-full sm:w-1/3 px-2 mb-4">
                    <ClientContextSelector
                        typeIn={[PartyType.Client]}
                        carryQueryArgs={true}
                        statusIn={[PartyStatusEnum.Confirmed, PartyStatusEnum.Inactive]}
                    />
                </div>
                {clientSelected ? (
                    <div className="w-full sm:w-1/3 px-2 mb-4">
                        <div className="mb-4 w-full select-client-control">
                            <Label name="accountId" value="Portfolio" />
                            <div className="flex items-center gap-2">
                                <select
                                    id="accountId"
                                    className="form-input min-w-0 flex-1"
                                    value={selectedAccount ? String(selectedAccount._id) : accountId || ""}
                                    onChange={(e) => applyAccountId(e.target.value)}
                                >
                                    <option value="">Select account</option>
                                    {selectableAccounts.map((account) => (
                                        <option key={String(account._id)} value={String(account._id)}>
                                            {accountOptionLabel(account)}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="shrink-0 text-sm text-sky-600 hover:underline px-2 py-1"
                                    onClick={handleClear}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
            {clientSelected ? (
                <div className="flex flex-wrap -mx-2">
                    <div className="border border-gray-300 rounded shadow p-4 bg-gray-100 w-full px-2 mb-4">
                        {client.firstTradeDate ? (
                            <div className="sticky top-0 z-20 -mx-4 px-4 py-3 mb-4 bg-gray-100/90 backdrop-blur border-b border-slate-200 print-none">
                                <div className="flex flex-wrap items-center gap-3">
                                    <PerformancePeriodSelector
                                        firstAvailableStartDate={client.firstTradeDate}
                                        endDate={endDate}
                                        defaultStartDate={defaultStartDate}
                                    />
                                    <label
                                        htmlFor="instrumentGrouping"
                                        className="inline-flex items-center gap-2 select-none text-sm text-slate-700"
                                        title="Group instruments when positions number more than 10"
                                    >
                                        <input
                                            id="instrumentGrouping"
                                            type="checkbox"
                                            checked={draftGroupInstruments}
                                            onChange={(e) => setDraftGroupInstruments(e.target.checked)}
                                            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                        />
                                        Instrument grouping
                                    </label>
                                    <label
                                        htmlFor="accountContribution"
                                        className="inline-flex items-center gap-2 select-none text-sm text-slate-700"
                                        title="Show each series as contribution to total account return"
                                    >
                                        <input
                                            id="accountContribution"
                                            type="checkbox"
                                            checked={draftAccountContribution}
                                            onChange={(e) => setDraftAccountContribution(e.target.checked)}
                                            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                        />
                                        Instrument contribution
                                    </label>
                                    {isLoadingPerformance ? <LoadingSpinner label="Loading" /> : null}
                                </div>
                            </div>
                        ) : isLoadingPerformance ? (
                            <div className="mb-4">
                                <LoadingSpinner label="Loading" />
                            </div>
                        ) : null}
                        {!selectedAccount ? (
                            <div>
                                <p className="text-gray-500 mb-0">Select a sum or physical account to view the performance report.</p>
                                {!effectivePerformanceData && !isLoadingPerformance ? (
                                    <div className="mt-2">
                                        <LoadingSpinner label="Loading client performance data…" />
                                    </div>
                                ) : null}
                            </div>
                        ) : errorPerformance ? (
                            <div>
                                <p>error performance:</p>
                                <pre> {JSON.stringify(errorPerformance, null, 2)}</pre>
                            </div>
                        ) : !effectivePerformanceData || !processedData ? (
                            isLoadingPerformance ? null : <LoadingSpinner label="Loading" />
                        ) : (
                            <AccountPerformanceReport
                                key={reportWorkKey}
                                client={client}
                                processedData={processedData}
                                account={selectedAccount}
                                startDate={startDate}
                                endDate={endDate}
                                groupInstruments={appliedGroupInstruments}
                                accountContribution={appliedAccountContribution}
                            />
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
